import { BaseType, CardinalDirection } from '../../../../types/Enums';
import { Building } from '../../../../types/protocols';
import { BaseComponentImpl, RoomImpl, LevelImpl } from '../../../../types/implementations';
import { ComponentWithReplacement } from '../types';
import { resolveCardinalDirection } from './cardinalDirectionResolver';
import { getCardinalDirectionOrder } from '../../../../Helpers/componentSortUtils';
import { translate, allDirectionsLabel } from '../../../../types/EnumTranslations';
import { getComponentRoomKey } from '../../../../Helpers/ComponentRoomUtils';
import { MOUNTING_SURFACE_LABELS, MountingSurface, utilityMountingSurface } from '../../../../Helpers/roofPlan';

const NO_TYPE_KEY = '__utan_typ__';
const NO_TYPE_LABEL = 'Utan typ';
const ALLA_KEY = '__alla__';
const ALLA_LABEL = 'Alla';

/**
 * Represents a subsection of components grouped by context (direction or room) and type.
 * Used for visual grouping and future mass-selection features.
 */
export interface Subsection {
    key: string;                    // e.g., "north_window_wood" or "room123_floor_parquet"
    contextKey: string;             // Direction enum value or room key
    contextLabel: string;           // "Nord" or "Kök"
    typeKey: string;                // specificType key
    typeLabel: string;              // Translated type name
    components: ComponentWithReplacement[];
}

type GroupingType = 'direction' | 'room' | null;

/**
 * Determines how components of a given base type should be grouped.
 */
function getGroupingType(baseType: BaseType): GroupingType {
    // Direction-based grouping for exterior components
    if ([BaseType.Roof, BaseType.RoofDrainage, BaseType.Facade, BaseType.SheetMetal, BaseType.Window, BaseType.Balcony].includes(baseType)) {
        return 'direction';
    }
    // Room-based grouping for interior components
    if ([BaseType.Floor, BaseType.Ceiling, BaseType.Wall, BaseType.Membrane, BaseType.Utility, BaseType.FixedFurnishing].includes(baseType)) {
        return 'room';
    }
    return null;
}

/** Context keys for utilities mounted on a roof or facade instead of sitting in a room. */
const MOUNTED_CONTEXT_KEYS: Record<MountingSurface, string> = {
    roof: '__mounted_roof__',
    facade: '__mounted_facade__',
};

/**
 * Gets the room key for a component.
 * Direct room assignment takes precedence, otherwise uses first parent room.
 * A utility mounted on a roof or facade (no room) gets a "Tak"/"Fasad" context instead.
 */
function getRoomKey(component: BaseComponentImpl, building: Building | null): string | null {
    // Direct room assignment (handles both plain string and ValueWithMetadata formats)
    const directRoomKey = getComponentRoomKey(component);
    if (directRoomKey) {
        return directRoomKey;
    }
    // Multi-room: use first room
    if (component.parentRoomKeys?.value?.length) {
        return component.parentRoomKeys.value[0];
    }
    const mounting = utilityMountingSurface(component, building?.model?.structure?.components);
    if (mounting) {
        return MOUNTED_CONTEXT_KEYS[mounting];
    }
    return null;
}

/**
 * Gets a display label for a cardinal direction.
 * Uses the EnumTranslations translate function for proper Swedish translations.
 */
function getDirectionLabel(key: string, baseType: BaseType): string {
    if (key === 'unassigned') {
        return allDirectionsLabel(baseType);
    }
    // Use the translate function from EnumTranslations for cardinal directions
    return translate(key as CardinalDirection);
}

/**
 * Gets a display label for a room.
 * Uses RoomImpl.displayName() prefixed with LevelImpl.displayName() if available.
 */
function getRoomLabel(key: string, building: Building | null, _getTranslation: (k: string) => string): string {
    if (key === 'unassigned') {
        return 'Ej tilldelat rum';
    }
    if (key === MOUNTED_CONTEXT_KEYS.roof) return MOUNTING_SURFACE_LABELS.roof;
    if (key === MOUNTED_CONTEXT_KEYS.facade) return MOUNTING_SURFACE_LABELS.facade;
    const roomData = building?.model?.structure?.rooms?.[key];
    if (!roomData) {
        return 'Okänt rum';
    }

    const room = new RoomImpl(roomData);
    const roomName = room.displayName();

    // Prefix with level display name if available
    if (roomData.levelKey) {
        const levelData = building?.model?.structure?.levels?.[roomData.levelKey];
        if (levelData) {
            const level = new LevelImpl(levelData);
            return `${level.displayName()} - ${roomName}`;
        }
    }

    return roomName;
}

/**
 * Gets the sort order for a context key.
 * Cardinal directions use clockwise order, rooms sort alphabetically (return 0).
 * Unassigned items always sort last.
 */
function getContextOrder(key: string, type: GroupingType): number {
    if (key === 'unassigned') {
        return 999; // Last
    }
    if (type === 'direction') {
        return getCardinalDirectionOrder(key as CardinalDirection);
    }
    // Mounted utilities after every room, before the unassigned ones
    if (key === MOUNTED_CONTEXT_KEYS.roof) return 997;
    if (key === MOUNTED_CONTEXT_KEYS.facade) return 998;
    return 0; // Rooms: alphabetical by label (handled in sort)
}

/**
 * Groups components into subsections based on context (direction or room) and specificType.
 * Every type gets its own subsection, even with a single component.
 * Returns an empty array if the base types don't support subsectioning.
 *
 * @param components - Components to group
 * @param baseTypes - Base types in this group (used to determine grouping strategy)
 * @param building - Building data for room lookups
 * @param getTranslation - Function to translate enum keys to display labels
 * @returns Array of subsections sorted by context order, then by type label
 */
export function groupBySubsection(
    components: ComponentWithReplacement[],
    baseTypes: BaseType[],
    building: Building | null,
    getTranslation: (key: string) => string
): Subsection[] {
    if (components.length === 0 || baseTypes.length === 0) {
        return [];
    }

    // Determine grouping from first baseType (groups are homogeneous in this regard)
    const groupingType = getGroupingType(baseTypes[0]);
    if (!groupingType) {
        // No direction/room grouping - create single "Alla" subsection for consistent hierarchy
        return [{
            key: ALLA_KEY,
            contextKey: ALLA_KEY,
            contextLabel: ALLA_LABEL,
            typeKey: ALLA_KEY,
            typeLabel: ALLA_LABEL,
            components
        }];
    }

    // Group by context (direction/room) then by specificType
    const groups = new Map<string, Subsection>();

    for (const comp of components) {
        const contextKey = groupingType === 'direction'
            ? resolveCardinalDirection(comp.component, building) ?? 'unassigned'
            : getRoomKey(comp.component, building) ?? 'unassigned';

        const typeKey = comp.component.specificType?.value || NO_TYPE_KEY;
        const key = `${contextKey}_${typeKey}`;

        if (!groups.has(key)) {
            const contextLabel = groupingType === 'direction'
                ? getDirectionLabel(contextKey, comp.component.type)
                : getRoomLabel(contextKey, building, getTranslation);

            groups.set(key, {
                key,
                contextKey,
                contextLabel,
                typeKey,
                typeLabel: typeKey === NO_TYPE_KEY ? NO_TYPE_LABEL : getTranslation(typeKey),
                components: []
            });
        }
        groups.get(key)!.components.push(comp);
    }

    // Sort: by context order, then by type label (components without a type last within context)
    return Array.from(groups.values()).sort((a, b) => {
        const contextOrder = getContextOrder(a.contextKey, groupingType) - getContextOrder(b.contextKey, groupingType);
        if (contextOrder !== 0) return contextOrder;
        if (a.typeKey === NO_TYPE_KEY) return 1;
        if (b.typeKey === NO_TYPE_KEY) return -1;
        return a.typeLabel.localeCompare(b.typeLabel, 'sv');
    });
}

/**
 * Groups subsections by their context key for rendering purposes.
 * This creates a two-level hierarchy: context → subsections
 */
export function groupSubsectionsByContext(subsections: Subsection[]): Map<string, Subsection[]> {
    const map = new Map<string, Subsection[]>();
    for (const sub of subsections) {
        const list = map.get(sub.contextKey) || [];
        list.push(sub);
        map.set(sub.contextKey, list);
    }
    return map;
}
